/**
 * @fileoverview Component to render owner names as clickable links that, when clicked,
 * filter vessels by ownership and set them as the selected output.
 * 
 * @module OwnerRenderer
 */
import React from 'react';
import { Link } from '@heroui/react';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { AppDispatch } from '../../../core/store';
import { ESelectionType } from '../../sharedEnums';
import { setSelectedVesselsForOutputRequest } from '../../../features/search/searchSlice';
import { filterGivenVesselsByFilterCriteriaForOutput } from '../../../features/search/helper';
import { selectSubscribedVesselSearchFormData } from '../../../features/search/searchSelectors';
import { showWarningAlert } from '../../../services/globalAlertService';

interface OwnerRendererProps {
	ownerName: string; // The name of the owner, which is used to filter vessels
	className?: string; // Optional, to allow custom styling
}

export const OwnerRenderer: React.FC<OwnerRendererProps> = ({ ownerName, className }) => {
	if (!ownerName) {
		return null;
	}

	const dispatch = useAppDispatch<AppDispatch>();

	const vessels = useAppSelector(selectSubscribedVesselSearchFormData);

	const onPress = () => {
		if (vessels && vessels.length > 0) {
			const companyFilterCriteria = { type: ESelectionType.Ownership, value: ownerName };
			const companyOutput = filterGivenVesselsByFilterCriteriaForOutput(vessels, companyFilterCriteria);
			dispatch(setSelectedVesselsForOutputRequest({ outputSelection: companyOutput }));
		} else {
			showWarningAlert('Owner not found');
		}
	};

	return (
		<Link
			onPress={onPress}
			underline="always"
			className={`text-msi-darkgrey-color-600 ${className}`}
			size="sm"
			title="Click to select owner as output"
		>
			<span className="cursor-pointer">{ownerName}</span>
		</Link>
	);
};

export default OwnerRenderer;
